import { t } from "@lingui/macro"
import { observer } from "mobx-react"
import { useMediaQuery, useTheme } from "@mui/material"
import { useCallback, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"

import { ContractsListStore } from "./store"
import { useContractColumns } from "./hooks/useContractColumns"
import { ContractListHeader } from "./components/ContractListHeader"
import { ContractListTable } from "./components/ContractListTable"

import { Repository } from "src/types/channel"

import { StoreProvider } from "src/store/lib/StoreProvider"
import { GlobalStore } from "src/store"
import { useStore } from "src/store/lib/useStore"
import { ListPage } from "src/components/ListPage"

import { trackModuleEvent } from "src/analytics/helpers/mixpanel_tracking"
import { MixpanelProperties } from "src/analytics/constants/properties"

const repository: Repository = "contracts"

const View = observer(() => {
    const store = useStore(ContractsListStore)
    const globalStore = useStore(GlobalStore)
    const columns = useContractColumns()
    const theme = useTheme()
    const smallScreen = useMediaQuery(theme.breakpoints.down("sm"))
    const navigate = useNavigate()

    useEffect(() => {
        const storedSegments = window.localStorage.getItem(
            "SelectedContractSegments",
        )
        if (storedSegments != null) {
            store.setSegments(JSON.parse(storedSegments))
        }
        void store.init(
            globalStore.session.accessGroupId,
            globalStore.session.user?.adminId,
        )
    }, [
        globalStore.session.accessGroupId,
        globalStore.session.user?.adminId,
        store,
    ])

    const handleSearch = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            await store.contracts.loadSearch(event.target.value)
        },
        [store.contracts],
    )

    const handleSegmentChange = useCallback(async (segments: number[]) => {
        window.localStorage.setItem(
            "SelectedContractSegments",
            JSON.stringify(segments),
        )
        await store.setSegments(segments)
        await store.contracts.loadInitialPage()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleDownloadContractAsCsv = useCallback(() => {
        trackModuleEvent("contracts | Export Contract Data", {
            [MixpanelProperties.UserID]: globalStore.session.user?.adminId,
            [MixpanelProperties.SegmentID]: store.segments,
        })
        return store.exportContract()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalStore.session.user?.adminId])

    const handleRowClick = useCallback(
        (item) => {
            trackModuleEvent("Contracts | Detailed View", {
                [MixpanelProperties.ItemID]: item.id,
                [MixpanelProperties.AccessGroupName]:
                    globalStore.session.accessGroup?.name,
                [MixpanelProperties.AccessGroupID]:
                    globalStore.session.accessGroup?.id,
            })
            navigate(`/contracts/${item.id}`)
        },
        [
            globalStore.session.accessGroup?.id,
            globalStore.session.accessGroup?.name,
            navigate,
        ],
    )

    const header = useMemo(
        () => ({
            header: t`contracts-list-view.header.contracts`,
            breadcrumbs: [t`contracts-list-view.header.contracts`],
        }),
        [],
    )

    const filter = useMemo(() => {
        return {
            actions: (
                <ContractListHeader
                    search={store.contracts.meta.search}
                    segments={store.segments}
                    onSearch={handleSearch}
                    onSegmentChange={handleSegmentChange}
                    onExportCsv={handleDownloadContractAsCsv}
                    isSmallScreen={smallScreen}
                />
            ),
        }
    }, [
        store.contracts.meta.search,
        store.segments,
        smallScreen,
        handleSearch,
        handleSegmentChange,
        handleDownloadContractAsCsv,
    ])

    const tableIsLoading =
        !store.contracts.meta.initialized ||
        globalStore.loading.is(ContractsListStore.LoadingKeys.pageLoad)

    return (
        <ListPage header={header} filter={filter} loading={tableIsLoading}>
            <ContractListTable
                paginator={store.contracts}
                data={store.contracts.items}
                columns={columns}
                repository={repository}
                loading={!store.contracts.meta.initialized}
                onRowClick={handleRowClick}
            />
        </ListPage>
    )
})

export const ContractListView = () => {
    return (
        <StoreProvider Store={ContractsListStore}>
            <View />
        </StoreProvider>
    )
}
